import SecureLS from 'secure-ls'

// Memory cache to reduce storage operations
let memoryCache = {
  token: null,
  user: null,
  fcmToken: null,
  lastUpdate: {},
  initialized: false
};

// Cache TTL (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

// Initialize SecureLS with error handling for iOS Safari compatibility
const initializeSecureLS = () => {
  try {
    // Test if localStorage is available and working (fails in iOS private browsing)
    const testKey = '__secure_ls_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return new SecureLS({encodingType: 'aes'});
  } catch (error) {
    console.warn('SecureLS initialization failed, falling back to sessionStorage:', error);
    return null;
  }
};

const ls = initializeSecureLS();

// Fallback storage functions for when SecureLS fails
const fallbackStorage = {
  set: (key, value) => {
    try {
      if (typeof value === 'object') {
        sessionStorage.setItem(key, JSON.stringify(value));
      } else {
        sessionStorage.setItem(key, value);
      }
      return true;
    } catch (error) {
      console.warn('Fallback storage set failed:', error);
      return false;
    }
  },
  get: (key) => {
    try {
      const value = sessionStorage.getItem(key);
      if (!value) return null;
      
      // Try to parse as JSON, fallback to string
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    } catch (error) {
      console.warn('Fallback storage get failed:', error);
      return null;
    }
  },
  remove: (key) => {
    try {
      sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn('Fallback storage remove failed:', error);
      return false;
    }
  }
};

// Optimized storage wrapper with memory caching
const safeStorage = {
  set: (key, value) => {
    let success = false;
    
    if (ls) {
      try {
        ls.set(key, value);
        success = true;
      } catch (error) {
        console.warn('SecureLS set failed, using fallback:', error);
        success = fallbackStorage.set(key, value);
      }
    } else {
      success = fallbackStorage.set(key, value);
    }

    // Update memory cache on successful storage
    if (success) {
      if (key === 'token') memoryCache.token = value;
      else if (key === 'user') memoryCache.user = value;
      else if (key === 'fcmToken') memoryCache.fcmToken = value;
      
      memoryCache.lastUpdate[key] = Date.now();
    }

    return success;
  },
  
  get: (key) => {
    const now = Date.now();
    
    // Return from memory cache if recent and valid
    if (memoryCache.lastUpdate[key] && 
        (now - memoryCache.lastUpdate[key] < CACHE_TTL)) {
      if (key === 'token' && memoryCache.token !== null) return memoryCache.token;
      if (key === 'user' && memoryCache.user !== null) return memoryCache.user;
      if (key === 'fcmToken' && memoryCache.fcmToken !== null) return memoryCache.fcmToken;
    }

    let value = null;
    
    if (ls) {
      try {
        value = ls.get(key);
      } catch (error) {
        console.warn('SecureLS get failed, using fallback:', error);
        value = fallbackStorage.get(key);
      }
    } else {
      value = fallbackStorage.get(key);
    }

    // Update memory cache
    if (value !== null) {
      if (key === 'token') memoryCache.token = value;
      else if (key === 'user') memoryCache.user = value;
      else if (key === 'fcmToken') memoryCache.fcmToken = value;
      
      memoryCache.lastUpdate[key] = now;
    }

    return value;
  },
  
  remove: (key) => {
    let success = false;
    
    if (ls) {
      try {
        ls.remove(key);
        success = true;
      } catch (error) {
        console.warn('SecureLS remove failed, using fallback:', error);
        success = fallbackStorage.remove(key);
      }
    } else {
      success = fallbackStorage.remove(key);
    }

    // Clear from memory cache
    if (success) {
      if (key === 'token') memoryCache.token = null;
      else if (key === 'user') memoryCache.user = null;
      else if (key === 'fcmToken') memoryCache.fcmToken = null;
      
      delete memoryCache.lastUpdate[key];
    }

    return success;
  },

  // Batch operations for better performance
  setBatch: (operations) => {
    const results = {};
    operations.forEach(({ key, value }) => {
      results[key] = safeStorage.set(key, value);
    });
    return results;
  },

  removeBatch: (keys) => {
    const results = {};
    keys.forEach(key => {
      results[key] = safeStorage.remove(key);
    });
    return results;
  }
};

// Clear memory cache
const clearMemoryCache = () => {
  memoryCache = {
    token: null,
    user: null,
    fcmToken: null,
    lastUpdate: {},
    initialized: false
  };
};

const key = 'token'
const location = 'geolocation'
const user = 'user'
const store = 'store'
const fcmToken = 'fcmToken'
const scheduleNotify = 'scheduleNotify'

// Optimized token operations
const storeToken = (token) => {
  try {
    const success = safeStorage.set(key, token);
    if (!success) {
      console.error('Failed to store auth token');
    }
    return success;
  } catch (error) {
    console.error('Error storing the auth token', error);
    return false;
  }
}

const getToken = () => {
  try {
    // First try memory cache
    if (memoryCache.token && memoryCache.lastUpdate[key] && 
        (Date.now() - memoryCache.lastUpdate[key] < CACHE_TTL)) {
      return memoryCache.token;
    }

    // Get from storage with fallback
    const lsToken = safeStorage.get(key);
    if (lsToken) return lsToken;
    
    // If not found, try sessionStorage directly (legacy support)
    const sessionToken = sessionStorage.getItem(key);
    if (sessionToken) {
      // Update cache for next time
      memoryCache.token = sessionToken;
      memoryCache.lastUpdate[key] = Date.now();
    }
    
    return sessionToken;
  } catch (error) {
    console.error('Error getting the auth token', error);
    return null;
  }
}

// Optimized user operations
const storeUser = (data) => {
  try {
    const userJson = typeof data === 'string' ? data : JSON.stringify(data);
    const success = safeStorage.set(user, userJson);
    if (!success) {
      console.error('Failed to store user data');
    }
    return success;
  } catch (error) {
    console.error('Error storing the user', error);
    return false;
  }
}

const getUser = async () => {
  try {
    // Check memory cache first
    if (memoryCache.user && memoryCache.lastUpdate[user] && 
        (Date.now() - memoryCache.lastUpdate[user] < CACHE_TTL)) {
      return memoryCache.user;
    }

    // Get from secure storage
    const lsUser = safeStorage.get(user);
    if (lsUser) return lsUser;
    
    // Fallback to sessionStorage (legacy support)
    const sessionUser = sessionStorage.getItem(user);
    if (sessionUser) {
      // Update cache
      memoryCache.user = sessionUser;
      memoryCache.lastUpdate[user] = Date.now();
    }
    
    return sessionUser;
  } catch (error) {
    console.error('Error getting the user', error);
    return null;
  }
}

// Optimized batch removal
const remove = async () => {
  try {
    // Clear memory cache first
    clearMemoryCache();
    
    // Batch remove operations
    const keysToRemove = ['user', 'uid', 'token', 'sid'];
    const results = safeStorage.removeBatch(keysToRemove);
    
    // Also clear sessionStorage for legacy support
    keysToRemove.forEach(key => {
      try {
        sessionStorage.removeItem(key);
      } catch (error) {
        console.warn(`Failed to remove ${key} from sessionStorage:`, error);
      }
    });
    
    return Object.values(results).every(success => success);
  } catch (error) {
    console.error('Error removing tokens', error);
    return false;
  }
}

// FCM Token operations
const storeFCM = (token) => {
  try {
    const success = safeStorage.set(fcmToken, token);
    if (!success) {
      console.error('Failed to store FCM token');
    }
    return success;
  } catch (error) {
    console.error('Error storing the fcm token', error);
    return false;
  }
}

const getFCMToken = () => {
  try {
    return safeStorage.get(fcmToken);
  } catch (error) {
    console.error('Error getting the fcm token', error);
    return null;
  }
}

// Schedule notification operations
const setScheduleNotify = (reminder) => {
  try {
    const success = safeStorage.set(scheduleNotify, reminder);
    if (!success) {
      console.error('Failed to store schedule notification');
    }
    return success;
  } catch (error) {
    console.error('Error storing the schedule notify', error);
    return false;
  }
}

const getScheduleNotify = () => {
  try {
    return safeStorage.get(scheduleNotify);
  } catch (error) {
    console.error('Error getting the schedule notify', error);
    return null;
  }
}

// Location operations
const storeLocation = (geolocation) => {
  try {
    const success = safeStorage.set(location, geolocation);
    if (!success) {
      console.error('Failed to store location');
    }
    return success;
  } catch (error) {
    console.error('Error storing the location', error);
    return false;
  }
}

const getLocation = () => {
  try {
    return safeStorage.get(location);
  } catch (error) {
    console.error('Error getting the location', error);
    return null;
  }
}

// Store operations
const setStore = (storeData) => {
  try {
    const success = safeStorage.set('sid', storeData);
    if (!success) {
      console.error('Failed to set store');
    }
    return success;
  } catch (error) {
    console.error('Error setting store', error);
    return false;
  }
}

const removeStore = async () => {
  try {
    const results = safeStorage.removeBatch([store, 'sid']);
    return Object.values(results).every(success => success);
  } catch (error) {
    console.error('Error removing store', error);
    return false;
  }
}

const getStore = () => {
  try {
    return safeStorage.get(store);
  } catch (error) {
    console.error('Error getting the store', error);
    return null;
  }
}

// Remember me operations
const storeRememberMe = (data) => {
  try {
    const success = safeStorage.set('rememberMe', data);
    if (!success) {
      console.error('Failed to store remember me');
    }
    return success;
  } catch (error) {
    console.error('Error storing remember me', error);
    return false;
  }
}

const getRememberMe = () => {
  try {
    return safeStorage.get('rememberMe');
  } catch (error) {
    console.error('Error getting remember me', error);
    return null;
  }
}

const removeRememberMe = () => {
  try {
    return safeStorage.remove('rememberMe');
  } catch (error) {
    console.error('Error removing remember me', error);
    return false;
  }
}

// Phone operations
const storeRememberPhone = (phone) => {
  try {
    const success = safeStorage.set('rememberPhone', phone);
    if (!success) {
      console.error('Failed to store remember phone');
    }
    return success;
  } catch (error) {
    console.error('Error storing remember phone', error);
    return false;
  }
}

const getRememberPhone = () => {
  try {
    return safeStorage.get('rememberPhone');
  } catch (error) {
    console.error('Error getting remember phone', error);
    return null;
  }
}

const removeRememberPhone = () => {
  try {
    return safeStorage.remove('rememberPhone');
  } catch (error) {
    console.error('Error removing remember phone', error);
    return false;
  }
}

// Utility functions
const clearCache = () => {
  clearMemoryCache();
};

const getCacheInfo = () => {
  return {
    memoryCache: { ...memoryCache },
    cacheSize: Object.keys(memoryCache.lastUpdate).length,
    oldestEntry: Math.min(...Object.values(memoryCache.lastUpdate))
  };
};

// Batch operations for better performance
const storeBatch = (operations) => {
  try {
    return safeStorage.setBatch(operations);
  } catch (error) {
    console.error('Error in batch storage operation', error);
    return {};
  }
};

const _expObject = {
  getToken,
  getUser,
  getLocation,
  remove,
  storeToken,
  storeUser,
  storeLocation,
  setStore,
  getStore,
  removeStore,
  storeFCM,
  getFCMToken,
  setScheduleNotify,
  getScheduleNotify,
  storeRememberMe,
  getRememberMe,
  storeRememberPhone,
  getRememberPhone,
  removeRememberMe,
  removeRememberPhone,
  // New utility functions
  clearCache,
  getCacheInfo,
  storeBatch
}

export default _expObject
